
import React, { useCallback, useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useAuth } from '../../context/AuthContext';
import { usePortal } from '../home/PortalContext';
import { getUdaThemeById, getUdaThemeBySlug, UDA_THEMES } from '../home/udaThemes';
import { TILE_URLS } from '@/config/mapTiles';

// State-wide view: the geographic centre of Andhra Pradesh. Used for state/DTCP
// users and as the Reset View target.
const AP_CENTER: [number, number] = [79.74, 15.9129];
const AP_ZOOM = 7;

const SRC_UDA = 'uda-home';
const LYR_UDA_FILL = 'uda-home-fill';
const LYR_UDA_LINE = 'uda-home-line';
const LYR_UDA_PT = 'uda-home-point';

// The wider region (district) the authority sits in. Drawn under the UDA
// boundary, because a UDA polygon on its own is town-sized — Srikakulam's is
// ~53 km² against a 4,442 km² district — and does not read as "the region".
const SRC_REGION = 'region-home';
const LYR_REGION_FILL = 'region-home-fill';
const LYR_REGION_LINE = 'region-home-line';

type MapStyle = 'osm' | 'satellite' | 'street' | 'hybrid';
type MeasureMode = null | 'line' | 'polygon';

const MAP_STYLES: Record<MapStyle, { url: string; label: string; icon: string }> = {
  osm: {
    url: TILE_URLS.osmPlain,
    label: 'OpenStreetMap',
    icon: 'ri-road-map-line',
  },
  satellite: {
    url: TILE_URLS.googleSatellite,
    label: 'Satellite',
    icon: 'ri-earth-line',
  },
  street: {
    url: TILE_URLS.googleRoads,
    label: 'Street',
    icon: 'ri-map-2-line',
  },
  hybrid: {
    url: TILE_URLS.googleHybrid,
    label: 'Hybrid',
    icon: 'ri-landscape-line',
  },
};

const MapButton: React.FC<{
  onClick: () => void;
  icon: string;
  title: string;
  className?: string;
}> = ({ onClick, icon, title, className = '' }) => (
  <button
    onClick={onClick}
    title={title}
    className={`w-10 h-10 rounded-full bg-white shadow-lg border border-stone-200 flex items-center justify-center text-stone-700 hover:bg-amber-50 hover:text-amber-600 hover:border-amber-300 active:scale-95 transition-all duration-150 ${className}`}
  >
    <i className={`${icon} text-lg`}></i>
  </button>
);

// --- Geo math helpers ---
function haversineDistance(a: [number, number], b: [number, number]): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const sinLat = Math.sin(dLat / 2);
  const sinLng = Math.sin(dLng / 2);
  const h = sinLat * sinLat + Math.cos(toRad(a[1])) * Math.cos(toRad(b[1])) * sinLng * sinLng;
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function polylineLength(coords: [number, number][]): number {
  let total = 0;
  for (let i = 1; i < coords.length; i++) {
    total += haversineDistance(coords[i - 1], coords[i]);
  }
  return total;
}

function polygonArea(coords: [number, number][]): number {
  if (coords.length < 3) return 0;
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  let area = 0;
  const ring = [...coords, coords[0]];
  for (let i = 0; i < ring.length - 1; i++) {
    const [lng1, lat1] = ring[i];
    const [lng2, lat2] = ring[i + 1];
    area += toRad(lng2 - lng1) * (2 + Math.sin(toRad(lat1)) + Math.sin(toRad(lat2)));
  }
  return Math.abs((area * R * R) / 2);
}

function formatLength(m: number): string {
  const ft = m * 3.28084;
  return `${m.toFixed(1)} m  |  ${ft.toFixed(1)} ft`;
}

function formatArea(sqm: number): string {
  const sqft = sqm * 10.7639;
  const sqyd = sqm * 1.19599;
  const acres = sqm * 0.000247105;
  return `${sqft.toFixed(1)} sq ft  |  ${sqyd.toFixed(1)} sq yd  |  ${acres.toFixed(3)} acres`;
}

// Source/layer IDs
const SRC_LINE = 'measure-line';
const SRC_POLY = 'measure-polygon';
const SRC_PTS = 'measure-points';
const SRC_LABELS = 'measure-labels';
const LYR_LINE = 'measure-line-layer';
const LYR_POLY_FILL = 'measure-polygon-fill';
const LYR_POLY_STROKE = 'measure-polygon-stroke';
const LYR_PTS = 'measure-points-layer';
const LYR_LABELS = 'measure-labels-layer';

function segmentLabel(a: [number, number], b: [number, number]): string {
  const m = haversineDistance(a, b);
  const ft = m * 3.28084;
  return `${m.toFixed(1)}m / ${ft.toFixed(1)}ft`;
}

// --- Overlay data ---
interface OverlayPoint { name: string; lng: number; lat: number }

const DISTRICTS: OverlayPoint[] = [
  { name: 'Anantapur', lng: 77.6006, lat: 14.6819 },
  { name: 'Anakapalli', lng: 83.0037, lat: 17.6910 },
  { name: 'Alluri Sitharama Raju', lng: 82.6632, lat: 18.0743 },
  { name: 'Annamayya', lng: 78.7514, lat: 14.0544 },
  { name: 'Bapatla', lng: 80.4670, lat: 15.9047 },
  { name: 'Chittoor', lng: 79.1003, lat: 13.2172 },
  { name: 'East Godavari', lng: 82.2475, lat: 16.9891 },
  { name: 'Eluru', lng: 81.0952, lat: 16.7107 },
  { name: 'Guntur', lng: 80.4365, lat: 16.3067 },
  { name: 'Kadapa', lng: 78.8241, lat: 14.4674 },
  { name: 'Kakinada', lng: 82.2475, lat: 16.9891 },
  { name: 'Konaseema', lng: 82.0048, lat: 16.5728 },
  { name: 'Krishna', lng: 81.1389, lat: 16.1875 },
  { name: 'Kurnool', lng: 78.0373, lat: 15.8281 },
  { name: 'Nandyal', lng: 78.4836, lat: 15.4788 },
  { name: 'NTR', lng: 80.6480, lat: 16.5062 },
  { name: 'Nellore', lng: 79.9865, lat: 14.4426 },
  { name: 'Palnadu', lng: 80.0487, lat: 16.2346 },
  { name: 'Parvathipuram Manyam', lng: 83.4261, lat: 18.7668 },
  { name: 'Prakasam', lng: 80.0499, lat: 15.5057 },
  { name: 'Sri Sathya Sai', lng: 77.8117, lat: 14.1654 },
  { name: 'Srikakulam', lng: 83.8938, lat: 18.2949 },
  { name: 'Tirupati', lng: 79.4192, lat: 13.6288 },
  { name: 'Visakhapatnam', lng: 83.2185, lat: 17.6868 },
  { name: 'Vizianagaram', lng: 83.3956, lat: 18.1067 },
  { name: 'West Godavari', lng: 81.1520, lat: 16.5921 },
];

const ULBS: OverlayPoint[] = [
  { name: 'GVMC Visakhapatnam', lng: 83.2185, lat: 17.6868 },
  { name: 'VMC Vijayawada', lng: 80.6480, lat: 16.5062 },
  { name: 'GMC Guntur', lng: 80.4365, lat: 16.3067 },
  { name: 'TMC Tirupati', lng: 79.4192, lat: 13.6288 },
  { name: 'Rajahmundry MC', lng: 81.8040, lat: 17.0005 },
  { name: 'Kakinada MC', lng: 82.2475, lat: 16.9891 },
  { name: 'Nellore MC', lng: 79.9865, lat: 14.4426 },
  { name: 'Kurnool MC', lng: 78.0373, lat: 15.8281 },
  { name: 'Kadapa MC', lng: 78.8241, lat: 14.4674 },
  { name: 'Anantapur MC', lng: 77.6006, lat: 14.6819 },
  { name: 'Eluru MC', lng: 81.0952, lat: 16.7107 },
  { name: 'Ongole MC', lng: 80.0499, lat: 15.5057 },
  { name: 'Srikakulam MC', lng: 83.8938, lat: 18.2949 },
  { name: 'Vizianagaram MC', lng: 83.3956, lat: 18.1067 },
  { name: 'Tenali MC', lng: 80.6400, lat: 16.2386 },
  { name: 'Narasaraopet MC', lng: 80.0487, lat: 16.2346 },
  { name: 'Proddatur MC', lng: 78.5481, lat: 14.7502 },
  { name: 'Hindupur MC', lng: 77.4932, lat: 13.8290 },
  { name: 'Nandyal MC', lng: 78.4836, lat: 15.4788 },
  { name: 'Mangalagiri MC', lng: 80.5686, lat: 16.4307 },
  { name: 'Machilipatnam MC', lng: 81.1389, lat: 16.1875 },
  { name: 'Chittoor MC', lng: 79.1003, lat: 13.2172 },
  { name: 'Adoni MC', lng: 77.2744, lat: 15.6267 },
  { name: 'Tadepalligudem MC', lng: 81.5268, lat: 16.8131 },
  { name: 'Chilakaluripet MC', lng: 80.1672, lat: 16.0892 },
];

/* The real UDAs, by THE UDA name (ds_uda_lst_t.uda_nm, filled into the themes at
   start-up by main.tsx) at each theme's centre. Read when the overlay is drawn,
   not at import, so the master's names are already in. */
const udaPoints = (): OverlayPoint[] =>
  Object.values(UDA_THEMES).map((t) => ({ name: t.name, lng: t.lng, lat: t.lat }));

type OverlayKey = 'districts' | 'ulbs' | 'udas';
const OVERLAY_CONFIG: Record<OverlayKey, { label: string; icon: string; color: string; data: OverlayPoint[] }> = {
  districts: { label: 'Districts', icon: 'ri-map-pin-2-line', color: '#2563eb', data: DISTRICTS },
  ulbs:      { label: 'ULBs',      icon: 'ri-building-line',  color: '#16a34a', data: ULBS },
  udas:      { label: 'UDAs',      icon: 'ri-building-2-line', color: '#d97706', get data() { return udaPoints(); } },
};

function toGeoJSON(points: OverlayPoint[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: points.map((p) => ({
      type: 'Feature' as const,
      geometry: { type: 'Point' as const, coordinates: [p.lng, p.lat] },
      properties: { name: p.name },
    })),
  };
}

export const GisMapPage: React.FC = () => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const { user } = useAuth();
  const { siteId } = usePortal();
  // Which UDA this map should open on. A logged-in user's own UDA wins; otherwise
  // fall back to the subdomain being browsed, so a public visitor on
  // suda.localhost lands on Srikakulam instead of the state-wide view. The slug in
  // public/geo/ap-udas.geojson is the UDA code lowercased (AMUDA -> amuda).
  // Null only for the state/DTCP portal with no logged-in UDA user.
  const udaSlug = user?.udaCode ? user.udaCode.toLowerCase() : (getUdaThemeById(siteId)?.slug ?? null);
  // The district to outline as "the region", taken from the same theme registry.
  const districtName = getUdaThemeBySlug(udaSlug)?.district ?? null;
  const homeRef = useRef<{ center: [number, number]; zoom: number }>({ center: AP_CENTER, zoom: AP_ZOOM });
  const [activeStyle, setActiveStyle] = useState<MapStyle>('osm');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showLayers, setShowLayers] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showMeasure, setShowMeasure] = useState(false);
  const [measureMode, setMeasureMode] = useState<MeasureMode>(null);
  const [liveResult, setLiveResult] = useState('');
  const [activeOverlays, setActiveOverlays] = useState<Set<OverlayKey>>(new Set());
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Measurement state refs (avoid stale closures)
  const pointsRef = useRef<[number, number][]>([]);
  const modeRef = useRef<MeasureMode>(null);
  const handlersRef = useRef<{ click: (e: maplibregl.MapMouseEvent) => void; mousemove: (e: maplibregl.MapMouseEvent) => void; dblclick: (e: maplibregl.MapMouseEvent) => void } | null>(null);

  // ---------- Map init ----------
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: {
        version: 8,
        sources: {
          osm: {
            type: 'raster',
            tiles: [MAP_STYLES.osm.url],
            tileSize: 256,
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
          },
        },
        layers: [
          {
            id: 'osm-tiles',
            type: 'raster',
            source: 'osm',
            minzoom: 0,
            maxzoom: 19,
          },
        ],
      },
      center: AP_CENTER,
      zoom: AP_ZOOM,
      doubleClickZoom: true,
    });

    map.current.addControl(
      new maplibregl.ScaleControl({ maxWidth: 200 }),
      'bottom-left'
    );

    // Add measurement sources & layers once map loads
    map.current.on('load', () => {
      const m = map.current!;
      m.addSource(SRC_LINE, { type: 'geojson', data: { type: 'Feature', geometry: { type: 'LineString', coordinates: [] }, properties: {} } });
      m.addSource(SRC_POLY, { type: 'geojson', data: { type: 'Feature', geometry: { type: 'Polygon', coordinates: [[]] }, properties: {} } });
      m.addSource(SRC_PTS, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      m.addSource(SRC_LABELS, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });

      m.addLayer({ id: LYR_POLY_FILL, type: 'fill', source: SRC_POLY, paint: { 'fill-color': '#d97706', 'fill-opacity': 0.15 } });
      m.addLayer({ id: LYR_POLY_STROKE, type: 'line', source: SRC_POLY, paint: { 'line-color': '#d97706', 'line-width': 2, 'line-dasharray': [2, 2] } });
      m.addLayer({ id: LYR_LINE, type: 'line', source: SRC_LINE, paint: { 'line-color': '#d97706', 'line-width': 3 } });
      m.addLayer({ id: LYR_PTS, type: 'circle', source: SRC_PTS, paint: { 'circle-radius': 5, 'circle-color': '#fff', 'circle-stroke-color': '#d97706', 'circle-stroke-width': 2 } });
      m.addLayer({
        id: LYR_LABELS, type: 'symbol', source: SRC_LABELS,
        layout: {
          'text-field': ['get', 'label'],
          'text-size': 12,
          'text-offset': [0, -1.2],
          'text-allow-overlap': true,
          'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
        },
        paint: {
          'text-color': '#78350f',
          'text-halo-color': '#fff',
          'text-halo-width': 2,
        },
      });

      // Overlay sources & layers (hidden by default)
      (Object.keys(OVERLAY_CONFIG) as OverlayKey[]).forEach((key) => {
        const cfg = OVERLAY_CONFIG[key];
        const srcId = `overlay-${key}`;
        m.addSource(srcId, { type: 'geojson', data: toGeoJSON(cfg.data) });
        m.addLayer({
          id: `overlay-${key}-circle`, type: 'circle', source: srcId,
          paint: { 'circle-radius': 6, 'circle-color': cfg.color, 'circle-stroke-color': '#fff', 'circle-stroke-width': 2 },
          layout: { visibility: 'none' },
        });
        m.addLayer({
          id: `overlay-${key}-label`, type: 'symbol', source: srcId,
          layout: {
            'text-field': ['get', 'name'],
            'text-size': 11,
            'text-offset': [0, 1.4],
            'text-allow-overlap': false,
            'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
            visibility: 'none',
          },
          paint: { 'text-color': cfg.color, 'text-halo-color': '#fff', 'text-halo-width': 1.5 },
        });
      });
    });

    return () => {
      map.current?.remove();
      map.current = null;
    };
  }, []);

  // ---------- Open on this portal's region ----------
  // Two outlines: the district (the REGION, highlighted and framed) and the UDA
  // boundary drawn on top of it. 20 of the 21 UDAs carry a boundary polygon in
  // ap-udas.geojson; only vk-pcpir is still a point and gets a marker instead.
  //
  // 19 of those are true jurisdiction outlines (1,468-17,358 km²), each the
  // union of that UDA's mandals — source of truth is
  // ds_dtcp.ds_uda_lst_t.bndry_plgn_jn, built by
  // server/migrations/019_uda_boundary_polygon_build.js, which also reports how
  // many mandals resolved (carried here as mandals_matched/mandals_total).
  // Geometry in this file is simplified to ~56 m for delivery; the DB holds
  // full precision.
  //
  // Two exceptions, both because ds_mandal_lst_t cannot supply the mandals:
  // apcrda has none mapped at all and keeps its original 62 km² municipal-core
  // polygon, so it draws far SMALLER than its peers; vk-pcpir resolved only
  // 53% (its units are villages, not mandals) and was left as a point rather
  // than written as a fragment.
  //
  // Nothing is masked out here, unlike the home page's location panel: this is an
  // interactive tool with pan, measurement and state-wide district/ULB/UDA
  // overlays, all of which would be hidden by an opaque surround.
  useEffect(() => {
    const m = map.current;
    if (!m || !udaSlug) return;
    let cancelled = false;

    // Bounding box of any GeoJSON geometry, by walking its coordinate nest.
    const bboxOf = (geometry: any): [number, number, number, number] | null => {
      let minX = 180, minY = 90, maxX = -180, maxY = -90;
      const walk = (c: any) => {
        if (typeof c[0] === 'number') {
          minX = Math.min(minX, c[0]); maxX = Math.max(maxX, c[0]);
          minY = Math.min(minY, c[1]); maxY = Math.max(maxY, c[1]);
        } else c.forEach(walk);
      };
      walk(geometry?.coordinates);
      return maxX > -180 ? [minX, minY, maxX, maxY] : null;
    };

    const apply = async () => {
      // ── The region: the district this UDA belongs to ──
      let regionBbox: [number, number, number, number] | null = null;
      if (districtName) {
        try {
          const fc = (await fetch('/geo/ap-districts.geojson').then(r => r.json())) as GeoJSON.FeatureCollection;
          if (cancelled || !map.current) return;
          const want = districtName.trim().toLowerCase();
          // Names differ between registries ('Nellore' vs 'Sri Potti Sriramulu
          // Nellore'), so match on containment rather than equality.
          const region =
            fc.features.find(f => String((f.properties as any)?.name || '').toLowerCase() === want) ||
            fc.features.find(f => {
              const have = String((f.properties as any)?.name || '').toLowerCase();
              return have.includes(want) || want.includes(have);
            });
          if (region?.geometry) {
            if (m.getLayer(LYR_REGION_FILL)) m.removeLayer(LYR_REGION_FILL);
            if (m.getLayer(LYR_REGION_LINE)) m.removeLayer(LYR_REGION_LINE);
            if (m.getSource(SRC_REGION)) m.removeSource(SRC_REGION);
            m.addSource(SRC_REGION, { type: 'geojson', data: region });
            m.addLayer({
              id: LYR_REGION_FILL, type: 'fill', source: SRC_REGION,
              paint: { 'fill-color': '#E66A1A', 'fill-opacity': 0.10 },
            });
            m.addLayer({
              id: LYR_REGION_LINE, type: 'line', source: SRC_REGION,
              paint: { 'line-color': '#CC5A12', 'line-width': 2, 'line-opacity': 0.8, 'line-dasharray': [3, 2] },
            });
            regionBbox = bboxOf(region.geometry);
          }
        } catch {
          // Region outline is an enhancement; the UDA view below still applies.
        }
      }

      // ── The authority's own boundary, over the region ──
      let feature: GeoJSON.Feature | undefined;
      try {
        const res = await fetch('/geo/ap-udas.geojson');
        const fc = (await res.json()) as GeoJSON.FeatureCollection;
        feature = fc.features.find((f) => (f.properties as any)?.slug === udaSlug);
      } catch {
        feature = undefined; // no boundary data — the region view still stands
      }
      if (cancelled || !map.current) return;

      if (feature) {
        [LYR_UDA_FILL, LYR_UDA_LINE, LYR_UDA_PT].forEach((id) => { if (m.getLayer(id)) m.removeLayer(id); });
        if (m.getSource(SRC_UDA)) m.removeSource(SRC_UDA);
        m.addSource(SRC_UDA, { type: 'geojson', data: feature });

        if (feature.geometry.type === 'Point') {
          m.addLayer({
            id: LYR_UDA_PT, type: 'circle', source: SRC_UDA,
            paint: { 'circle-radius': 9, 'circle-color': '#E66A1A', 'circle-opacity': 0.85, 'circle-stroke-color': '#fff', 'circle-stroke-width': 3 },
          });
        } else {
          m.addLayer({
            id: LYR_UDA_FILL, type: 'fill', source: SRC_UDA,
            paint: { 'fill-color': '#E66A1A', 'fill-opacity': 0.28 },
          });
          m.addLayer({
            id: LYR_UDA_LINE, type: 'line', source: SRC_UDA,
            paint: { 'line-color': '#CC5A12', 'line-width': 2.5, 'line-opacity': 0.95 },
          });
        }
      }

      // Frame the region when there is one — "show that region" — and fall back
      // to the authority's own extent otherwise.
      const target = regionBbox || (feature ? bboxOf(feature.geometry as any) : null);
      if (!target) return;
      m.once('moveend', () => {
        if (!cancelled && map.current) homeRef.current = { center: [m.getCenter().lng, m.getCenter().lat], zoom: m.getZoom() };
      });
      m.fitBounds([[target[0], target[1]], [target[2], target[3]]], { padding: 60, duration: 1200 });
    };

    if (m.loaded()) apply();
    else m.once('load', apply);

    return () => { cancelled = true; };
  }, [udaSlug, districtName]);

  // ---------- Fullscreen listener ----------
  useEffect(() => {
    const handleChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleChange);
    return () => document.removeEventListener('fullscreenchange', handleChange);
  }, []);

  // ---------- Basic controls ----------
  const handleZoomIn = useCallback(() => { map.current?.zoomIn({ duration: 300 }); }, []);
  const handleZoomOut = useCallback(() => { map.current?.zoomOut({ duration: 300 }); }, []);
  const handleFullscreen = useCallback(() => {
    if (!wrapperRef.current) return;
    if (document.fullscreenElement) document.exitFullscreen();
    else wrapperRef.current.requestFullscreen();
  }, []);

  const switchStyle = (style: MapStyle) => {
    if (!map.current || style === activeStyle) return;
    setActiveStyle(style);
    const source = map.current.getSource('osm') as maplibregl.RasterTileSource;
    if (source) source.setTiles([MAP_STYLES[style].url]);
  };

  const toggleOverlay = useCallback((key: OverlayKey) => {
    const m = map.current;
    if (!m) return;
    setActiveOverlays((prev) => {
      const next = new Set(prev);
      const visible = next.has(key);
      if (visible) next.delete(key); else next.add(key);
      const vis = visible ? 'none' : 'visible';
      m.setLayoutProperty(`overlay-${key}-circle`, 'visibility', vis);
      m.setLayoutProperty(`overlay-${key}-label`, 'visibility', vis);
      return next;
    });
  }, []);

  // ---------- Measurement helpers ----------
  const updateGeoJSON = useCallback((points: [number, number][], cursor?: [number, number]) => {
    const m = map.current;
    if (!m) return;
    const mode = modeRef.current;
    const allPts = cursor ? [...points, cursor] : points;

    // Points
    (m.getSource(SRC_PTS) as maplibregl.GeoJSONSource)?.setData({
      type: 'FeatureCollection',
      features: points.map((p) => ({ type: 'Feature' as const, geometry: { type: 'Point' as const, coordinates: p }, properties: {} })),
    });

    // Segment labels at midpoints
    const labelFeatures: GeoJSON.Feature[] = [];
    for (let i = 1; i < allPts.length; i++) {
      const a = allPts[i - 1];
      const b = allPts[i];
      const mid: [number, number] = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
      labelFeatures.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: mid },
        properties: { label: segmentLabel(a, b) },
      });
    }
    (m.getSource(SRC_LABELS) as maplibregl.GeoJSONSource)?.setData({
      type: 'FeatureCollection',
      features: labelFeatures,
    });

    if (mode === 'line') {
      (m.getSource(SRC_LINE) as maplibregl.GeoJSONSource)?.setData({
        type: 'Feature', geometry: { type: 'LineString', coordinates: allPts }, properties: {},
      });
      (m.getSource(SRC_POLY) as maplibregl.GeoJSONSource)?.setData({
        type: 'Feature', geometry: { type: 'Polygon', coordinates: [[]] }, properties: {},
      });
    } else if (mode === 'polygon') {
      const ring = allPts.length >= 3 ? [...allPts, allPts[0]] : [];
      (m.getSource(SRC_POLY) as maplibregl.GeoJSONSource)?.setData({
        type: 'Feature', geometry: { type: 'Polygon', coordinates: [ring] }, properties: {},
      });
      (m.getSource(SRC_LINE) as maplibregl.GeoJSONSource)?.setData({
        type: 'Feature', geometry: { type: 'LineString', coordinates: allPts }, properties: {},
      });
    }
  }, []);

  const clearMeasurement = useCallback(() => {
    const m = map.current;
    if (!m) return;
    pointsRef.current = [];
    (m.getSource(SRC_LINE) as maplibregl.GeoJSONSource)?.setData({ type: 'Feature', geometry: { type: 'LineString', coordinates: [] }, properties: {} });
    (m.getSource(SRC_POLY) as maplibregl.GeoJSONSource)?.setData({ type: 'Feature', geometry: { type: 'Polygon', coordinates: [[]] }, properties: {} });
    (m.getSource(SRC_PTS) as maplibregl.GeoJSONSource)?.setData({ type: 'FeatureCollection', features: [] });
    (m.getSource(SRC_LABELS) as maplibregl.GeoJSONSource)?.setData({ type: 'FeatureCollection', features: [] });
    setLiveResult('');
  }, []);

  const stopMeasureMode = useCallback(() => {
    const m = map.current;
    if (!m) return;
    if (handlersRef.current) {
      m.off('click', handlersRef.current.click);
      m.off('mousemove', handlersRef.current.mousemove);
      m.off('dblclick', handlersRef.current.dblclick);
      handlersRef.current = null;
    }
    m.getCanvas().style.cursor = '';
    m.doubleClickZoom.enable();
    clearMeasurement();
    modeRef.current = null;
    setMeasureMode(null);
  }, [clearMeasurement]);

  const startMeasureMode = useCallback((mode: 'line' | 'polygon') => {
    const m = map.current;
    if (!m) return;

    // Stop any existing measurement
    stopMeasureMode();

    modeRef.current = mode;
    setMeasureMode(mode);
    setShowMeasure(false);
    pointsRef.current = [];
    m.getCanvas().style.cursor = 'crosshair';
    m.doubleClickZoom.disable();

    const onClick = (e: maplibregl.MapMouseEvent) => {
      const pt: [number, number] = [e.lngLat.lng, e.lngLat.lat];
      pointsRef.current = [...pointsRef.current, pt];
      updateGeoJSON(pointsRef.current);

      if (modeRef.current === 'line') {
        setLiveResult(formatLength(polylineLength(pointsRef.current)));
      } else if (modeRef.current === 'polygon' && pointsRef.current.length >= 3) {
        setLiveResult(formatArea(polygonArea(pointsRef.current)));
      }
    };

    const onMouseMove = (e: maplibregl.MapMouseEvent) => {
      if (pointsRef.current.length === 0) return;
      const cursor: [number, number] = [e.lngLat.lng, e.lngLat.lat];
      updateGeoJSON(pointsRef.current, cursor);

      const allPts = [...pointsRef.current, cursor];
      if (modeRef.current === 'line') {
        setLiveResult(formatLength(polylineLength(allPts)));
      } else if (modeRef.current === 'polygon' && allPts.length >= 3) {
        setLiveResult(formatArea(polygonArea(allPts)));
      }
    };

    const onDblClick = (e: maplibregl.MapMouseEvent) => {
      e.preventDefault();
      // Finalize — keep the drawing and result, but stop adding points
      if (handlersRef.current) {
        m.off('click', handlersRef.current.click);
        m.off('mousemove', handlersRef.current.mousemove);
        m.off('dblclick', handlersRef.current.dblclick);
        handlersRef.current = null;
      }
      m.getCanvas().style.cursor = '';
      m.doubleClickZoom.enable();

      // Remove the last duplicate point from dblclick
      if (pointsRef.current.length > 1) {
        pointsRef.current = pointsRef.current.slice(0, -1);
        updateGeoJSON(pointsRef.current);
      }

      if (modeRef.current === 'line') {
        setLiveResult(formatLength(polylineLength(pointsRef.current)));
      } else if (modeRef.current === 'polygon' && pointsRef.current.length >= 3) {
        setLiveResult(formatArea(polygonArea(pointsRef.current)));
      }
      modeRef.current = null;
      setMeasureMode(null);
    };

    handlersRef.current = { click: onClick, mousemove: onMouseMove, dblclick: onDblClick };
    m.on('click', onClick);
    m.on('mousemove', onMouseMove);
    m.on('dblclick', onDblClick);
  }, [stopMeasureMode, updateGeoJSON]);

  const closeAllPanels = () => { setShowLayers(false); setShowSettings(false); setShowMeasure(false); };

  return (
    <div className="flex flex-col h-[calc(100vh-170px)]">
      {/* Map Container */}
      <div ref={wrapperRef} className="relative flex-1">
        <div ref={mapContainer} className="absolute inset-0" />

        {/* Live Measurement Result */}
        {liveResult && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg border border-amber-200 px-4 py-2 flex items-center gap-3">
            <i className={`${measureMode === 'polygon' || (!measureMode && pointsRef.current.length >= 3) ? 'ri-shape-line' : 'ri-ruler-line'} text-amber-600`}></i>
            <span className="font-semibold text-stone-800 text-sm">{liveResult}</span>
            <button
              onClick={() => { stopMeasureMode(); clearMeasurement(); }}
              title="Clear"
              className="w-6 h-6 rounded-full bg-stone-100 hover:bg-red-100 flex items-center justify-center text-stone-500 hover:text-red-500 transition-colors"
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
        )}

        {/* Custom Controls */}
        <div className="absolute top-4 right-3 z-10 flex flex-col gap-2 items-center">
          {/* Zoom In / Out - oval pill */}
          <div className="bg-white rounded-full shadow-lg border border-stone-200 flex flex-col overflow-hidden">
            <button
              onClick={handleZoomIn}
              title="Zoom in"
              className="w-10 h-10 flex items-center justify-center text-stone-700 hover:bg-amber-50 hover:text-amber-600 active:scale-95 transition-all duration-150"
            >
              <i className="ri-add-line text-lg"></i>
            </button>
            <div className="h-px bg-stone-200 mx-2"></div>
            <button
              onClick={handleZoomOut}
              title="Zoom out"
              className="w-10 h-10 flex items-center justify-center text-stone-700 hover:bg-amber-50 hover:text-amber-600 active:scale-95 transition-all duration-150"
            >
              <i className="ri-subtract-line text-lg"></i>
            </button>
          </div>

          {/* Fullscreen */}
          <MapButton
            onClick={handleFullscreen}
            icon={isFullscreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'}
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          />

          {/* Layers */}
          <div className="relative">
            <MapButton
              onClick={() => { setShowLayers(!showLayers); setShowSettings(false); setShowMeasure(false); }}
              icon="ri-stack-line"
              title="Layers"
              className={showLayers ? '!bg-amber-50 !text-amber-600 !border-amber-300' : ''}
            />
            {showLayers && (
              <div className="absolute top-0 right-12 bg-white rounded-lg shadow-lg border border-stone-200 overflow-hidden w-48">
                {/* Basemap section */}
                <div className="px-3 py-2 text-xs font-semibold text-stone-500 border-b border-stone-100 uppercase tracking-wide">Basemap</div>
                {(Object.keys(MAP_STYLES) as MapStyle[]).map((style) => (
                  <button
                    key={style}
                    onClick={() => switchStyle(style)}
                    className={`block w-full px-3 py-2 text-sm font-medium text-left transition-colors ${
                      activeStyle === style
                        ? 'bg-amber-600 text-white'
                        : 'text-stone-700 hover:bg-amber-50'
                    }`}
                  >
                    <i className={`${MAP_STYLES[style].icon} mr-2`}></i>
                    {MAP_STYLES[style].label}
                  </button>
                ))}
                {/* Overlay layers section */}
                <div className="px-3 py-2 text-xs font-semibold text-stone-500 border-b border-stone-100 border-t uppercase tracking-wide">Overlays</div>
                {(Object.keys(OVERLAY_CONFIG) as OverlayKey[]).map((key) => {
                  const cfg = OVERLAY_CONFIG[key];
                  const isActive = activeOverlays.has(key);
                  return (
                    <label
                      key={key}
                      className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={isActive}
                        onChange={() => toggleOverlay(key)}
                        className="w-4 h-4 rounded border-stone-300 accent-amber-600 cursor-pointer"
                      />
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: cfg.color }}></span>
                      <span>{cfg.label}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* Settings */}
          <div className="relative">
            <MapButton
              onClick={() => { setShowSettings(!showSettings); setShowLayers(false); setShowMeasure(false); }}
              icon="ri-settings-3-line"
              title="Settings"
              className={showSettings ? '!bg-amber-50 !text-amber-600 !border-amber-300' : ''}
            />
            {showSettings && (
              <div className="absolute top-0 right-12 bg-white rounded-lg shadow-lg border border-stone-200 overflow-hidden w-52">
                <div className="px-3 py-2 text-xs font-semibold text-stone-500 border-b border-stone-100 uppercase tracking-wide">Settings</div>
                {/* Basemap Dropdown */}
                <div className="px-3 py-2 border-b border-stone-100">
                  <label className="block text-xs font-medium text-stone-500 mb-1">Basemap</label>
                  <select
                    value={activeStyle}
                    onChange={(e) => { switchStyle(e.target.value as MapStyle); }}
                    className="w-full px-2 py-1.5 text-sm border border-stone-200 rounded-md bg-white text-stone-700 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none cursor-pointer"
                  >
                    {(Object.keys(MAP_STYLES) as MapStyle[]).map((style) => (
                      <option key={style} value={style}>{MAP_STYLES[style].label}</option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={() => { map.current?.setCenter(homeRef.current.center); map.current?.setZoom(homeRef.current.zoom); setShowSettings(false); }}
                  className="block w-full px-3 py-2 text-sm font-medium text-left text-stone-700 hover:bg-amber-50 transition-colors"
                >
                  <i className="ri-focus-3-line mr-2"></i>Reset View
                </button>
                <button
                  onClick={() => {
                    if (navigator.geolocation) {
                      navigator.geolocation.getCurrentPosition((pos) => {
                        map.current?.flyTo({ center: [pos.coords.longitude, pos.coords.latitude], zoom: 14, duration: 1000 });
                      });
                    }
                    setShowSettings(false);
                  }}
                  className="block w-full px-3 py-2 text-sm font-medium text-left text-stone-700 hover:bg-amber-50 transition-colors"
                >
                  <i className="ri-crosshair-2-line mr-2"></i>My Location
                </button>
              </div>
            )}
          </div>

          {/* Measure */}
          <div className="relative">
            <MapButton
              onClick={() => {
                if (measureMode) { stopMeasureMode(); return; }
                setShowMeasure(!showMeasure); setShowLayers(false); setShowSettings(false);
              }}
              icon="ri-ruler-line"
              title="Measure"
              className={showMeasure || measureMode ? '!bg-amber-50 !text-amber-600 !border-amber-300' : ''}
            />
            {showMeasure && !measureMode && (
              <div className="absolute top-0 right-12 bg-white rounded-lg shadow-lg border border-stone-200 overflow-hidden w-44">
                <div className="px-3 py-2 text-xs font-semibold text-stone-500 border-b border-stone-100 uppercase tracking-wide">Measure</div>
                <button
                  onClick={() => startMeasureMode('line')}
                  className="block w-full px-3 py-2 text-sm font-medium text-left text-stone-700 hover:bg-amber-50 transition-colors"
                >
                  <i className="ri-ruler-line mr-2"></i>Distance
                </button>
                <button
                  onClick={() => startMeasureMode('polygon')}
                  className="block w-full px-3 py-2 text-sm font-medium text-left text-stone-700 hover:bg-amber-50 transition-colors"
                >
                  <i className="ri-shape-line mr-2"></i>Area
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Measurement instructions */}
        {measureMode && pointsRef.current.length === 0 && (
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 bg-stone-800/80 text-white text-sm rounded-lg px-4 py-2 backdrop-blur-sm">
            Click on the map to start measuring. Double-click to finish.
          </div>
        )}
      </div>
    </div>
  );
};
